import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUsers, FaCheck, FaTimes } from "react-icons/fa";
import Select from "react-select";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./AjouterTroupeau.css";

export default function AjouterTroupeau() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [troupeaux, setTroupeaux] = useState([]);
  const [bergers, setBergers] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    nom: "",
    berger: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    nom: false,
    berger: false,
  });

  const [newTroupeau, setNewTroupeau] = useState({
    nom: "",
    berger: null,
  });

  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [troupeauxRes, bergersRes] = await Promise.all([
          api.get("/api/troupeaux/"),
          api.get("/api/bergers/"),
        ]);
        
        // Gérer la réponse de l'API (peut être un tableau ou un objet avec pagination)
        const troupeaux = Array.isArray(troupeauxRes.data) ? troupeauxRes.data : (troupeauxRes.data.results || []);
        const bergers = Array.isArray(bergersRes.data) ? bergersRes.data : (bergersRes.data.results || []);
        
        setTroupeaux(troupeaux);
        setBergers(bergers);
      } catch (err) {
        console.error("Erreur lors du chargement :", err);
      }
    };
    fetchData();
  }, []);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "nom" || name === "berger") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewTroupeau({ ...newTroupeau, [name]: value });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      nom: true,
      berger: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      nom: validateField("nom", newTroupeau.nom),
      berger: validateField("berger", newTroupeau.berger),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      // Préparer les données pour l'API
      const dataToSubmit = {
        nom: newTroupeau.nom,
        berger: newTroupeau.berger,
      };
      await api.post("/api/troupeaux/", dataToSubmit);
      setShowConfirmModal(false);
      toast.success("Troupeau ajouté avec succès !");
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/troupeaux");
      }, 500);
    } catch (err) {
      console.error(err);
      
      // Helper function to safely extract error messages
      const extractErrorMessage = (data) => {
        // If data is null/undefined, return null
        if (!data) return null;
        
        // If data.detail or data.message exist, use them
        if (data.detail) return String(data.detail);
        if (data.message) return String(data.message);
        
        // If data is a string, use it directly
        if (typeof data === 'string') return data.trim() || null;
        
        // If data is a plain object, iterate through values
        if (data && typeof data === 'object' && !Array.isArray(data) && data !== null) {
          const messages = [];
          
          for (const value of Object.values(data)) {
            if (Array.isArray(value)) {
              // Join array elements
              const arrayParts = value
                .map(item => String(item).trim())
                .filter(item => item !== '');
              if (arrayParts.length > 0) {
                messages.push(arrayParts.join(', '));
              }
            } else if (value !== null && value !== undefined) {
              // Convert other values to string
              const strValue = String(value).trim();
              if (strValue !== '') {
                messages.push(strValue);
              }
            }
          }
          
          if (messages.length > 0) {
            return messages.join(', ');
          }
        }
        
        return null;
      };
      
      const errorMessage = extractErrorMessage(err.response?.data) || 
                          "Erreur lors de l'ajout du troupeau !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Fonction pour obtenir le nom complet d'un berger
  const getBergerDisplayName = (id) => {
    if (!id) return "Non sélectionné";
    const berger = bergers.find(b => b.id === Number(id));
    if (!berger) return "Non sélectionné";
    return berger.nom_complet || `${berger.prenom || ""} ${berger.nom || ""}`.trim() || "Non sélectionné";
  };

  // Vérifier si tous les champs obligatoires sont remplis
  const isFormValid = () => {
    return newTroupeau.nom.trim() !== "" && newTroupeau.berger !== null;
  };

  return (
    <div className="ajouter-troupeau-page">
      <div className="title-badge">
        <FaUsers className="page-title-icon" aria-hidden="true" />
        <span>{t.addHerdTitle}</span>
      </div>
      
      <form onSubmit={handleSubmit} className="troupeau-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="nom">
              {t.herdNameLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              placeholder={t.herdNameLabel}
              value={newTroupeau.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.nom && touchedFields.nom ? "input-error" : ""}
              required
            />
            {fieldErrors.nom && touchedFields.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="berger">
              {t.shepherdField} <span className="required-indicator">*</span>
            </label>
            <div className={fieldErrors.berger && touchedFields.berger ? "react-select-error" : ""}>
              <Select
                name="berger"
                inputId="berger"
                value={
                  newTroupeau.berger
                    ? { value: newTroupeau.berger, label: getBergerDisplayName(newTroupeau.berger) }
                    : null
                }
                onChange={(selectedOption) => {
                  setNewTroupeau({ ...newTroupeau, berger: selectedOption ? selectedOption.value : null });
                  // Valider après changement
                  if (touchedFields.berger || fieldErrors.berger) {
                    const error = validateField("berger", selectedOption ? selectedOption.value : null);
                    setFieldErrors(prev => ({
                      ...prev,
                      berger: error
                    }));
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, berger: true }));
                  const error = validateField("berger", newTroupeau.berger);
                  setFieldErrors(prev => ({ ...prev, berger: error }));
                }}
                options={bergers.map((berger) => {
                  const displayName = berger.nom_complet || `${berger.prenom || ""} ${berger.nom || ""}`.trim() || t.noName;
                  return {
                    value: berger.id,
                    label: displayName
                  };
                })}
                isClearable
                className={fieldErrors.berger && touchedFields.berger ? "react-select-error" : ""}
                classNamePrefix="react-select"
                placeholder={t.selectShepherd}
              />
            </div>
            {fieldErrors.berger && touchedFields.berger && (
              <span className="field-error-message">{fieldErrors.berger}</span>
            )}
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? t.registering : t.register}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/troupeaux")}
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer l'ajout du troupeau</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer l'ajout :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Nom du troupeau :</span>
                  <span className="summary-value">{newTroupeau.nom || "Non renseigné"}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Berger (terrain) :</span>
                  <span className="summary-value">{getBergerDisplayName(newTroupeau.berger)}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
